import { CalendarX, ShieldAlert } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@wundo/ui';
import { m } from '@/paraglide/messages.js';

export interface CalendarLinkErrorDialogProps {
  /** The `?error=` code the provider bounced back with. Null keeps the dialog closed. */
  code: string | null;
  onClose: () => void;
}

/**
 * The provider's refusal, told face-on rather than in a line above the page.
 *
 * One code gets its own words: a calendar already on another Wundo account is
 * not fixed by trying again, which is exactly what the generic message says to
 * do. Every other code — refused consent, admin approval pending — shares the
 * generic refusal, the same story onboarding tells for them.
 */
export function CalendarLinkErrorDialog({ code, onClose }: CalendarLinkErrorDialogProps) {
  const conflict = code === 'account_already_linked_to_different_user';
  const Icon = conflict ? CalendarX : ShieldAlert;

  return (
    <Dialog open={code !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size='md'>
        {/*
         * Centered, icon first: this dialog carries one fact and one way out,
         * so it reads as a notice rather than a form — no header bar, no
         * cancel/confirm pair.
         */}
        <div className='flex flex-col items-center gap-3 px-7 pt-8 pb-4 text-center'>
          <span className='flex size-12 items-center justify-center rounded-full bg-danger-fill'>
            <Icon className='size-6 text-danger' aria-hidden='true' />
          </span>
          <DialogTitle>
            {conflict
              ? m.connections_link_conflict_title()
              : m.connections_link_failed_title()}
          </DialogTitle>
          <DialogDescription className='max-w-90'>
            {conflict ? m.connections_link_conflict() : m.connections_link_failed()}
          </DialogDescription>
        </div>

        <DialogFooter className='justify-center'>
          <Button onClick={onClose}>{m.connections_link_error_close()}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
